<!--
Copyright 2025 Google Inc. All rights reserved.

Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
-->
<!-- modified for WISPR-lab/data-export-gui -->
<template>
  <div>
    <v-alert
      v-model="showBanner"
      dense
      dismissible
      type="info"
    >
      Data may be incomplete. Some data exports are still loading.
    </v-alert>

    <v-dialog v-model="exportDialog" width="700">
      <v-card flat class="pa-5">
        <v-progress-circular indeterminate size="20" width="1"></v-progress-circular>
        <span class="ml-5">Exporting {{ totalHits }} events</span>
      </v-card>
    </v-dialog>

    <!-- <v-dialog v-model="saveSearchMenu" v-if="!disableSaveSearch" width="500">
      <v-card class="pa-4">
        <h3>Save Search</h3>
        <br />
        <v-text-field
          clearable
          v-model="saveSearchFormName"
          required
          placeholder="Name your saved search"
          outlined
          dense
          autofocus
          @focus="$event.target.select()"
          :rules="saveSearchNameRules"
        >
        </v-text-field>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="saveSearchMenu = false"> Cancel </v-btn>
          <v-btn
            text
            color="primary"
            @click="saveSearch"
            :disabled="!saveSearchFormName || saveSearchFormName.length > 255"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog> -->

    <div v-if="!eventList.objects.length && !searchInProgress && !currentQueryString">
      <welcome-card></welcome-card>
    </div>

    <div v-if="!eventList.objects.length && !searchInProgress && currentQueryString" class="ml-3">
      <search-not-found-card
        :currentQueryString="currentQueryString"
        :filterChips="filterChips"
        :disableSaveSearch="disableSaveSearch"
        @save-search-clicked="saveSearchMenu = true"
      ></search-not-found-card>
    </div>

    <!-- DISABLED: Row highlighting feature
    <div v-if="highlightEvent" class="mt-4">
      <strong>Showing context for event:</strong>
      <v-sheet class="d-flex flex-wrap mt-1 mb-5">
        <v-sheet class="flex-1-0">
          <span style="width: 200px" v-bind:style="getDataExportColor(highlightEvent)" class="datetime-table-cell pa-2">
            <span v-if="(highlightEvent._source.primary_timestamp !== null && highlightEvent._source.primary_timestamp !== '') || (highlightEvent._source.timestamp !== null && highlightEvent._source.timestamp !== '')">
              {{ highlightEvent._source.primary_timestamp | formatTimestamp | toISO8601 }}
            </span>
            <span v-else style="font-style: italic">undated</span>
          </span>
        </v-sheet>

        <v-sheet class="">
          <span class="datetime-table-cell pa-2">
            {{ highlightEvent._source.message }}
          </span>
        </v-sheet>
      </v-sheet>
    </div>
    -->

    <div class="event-list-container">
      <v-card
        v-if="(eventList.objects.length > 0 || searchInProgress) && userSettings.eventSummarization && !eventList.meta.summaryError"
        class="ts-ai-summary-card"
        outlined
      >
        <v-card-title class="ts-ai-summary-card-title">
          <v-btn icon small @click="toggleSummary" class="ts-ai-summary-fold-btn">
            <v-icon>{{ summaryCollapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
          </v-btn>
          <v-icon small color="primary" class="ml-1 mr-2 ts-ai-summary-icon">mdi-shimmer</v-icon>
          <div class="ts-ai-summary-text-group">
            <span class="ts-ai-summary-title">AI Summary</span>
            <span v-if="eventList.objects.length > 0" class="ts-ai-summary-subtitle">
              (for {{ eventList.objects.length }} events in this view)
            </span>
          </div>
          <v-btn icon small class="ml-1 ts-ai-summary-info-btn" :title="summaryInfoMessage">
            <v-icon small>mdi-information-outline</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text v-show="!summaryCollapsed" class="ts-ai-summary-text">
          <div v-if="isSummaryLoading || !eventList.meta.summary">
            <div class="ts-summary-placeholder-line shimmer"></div>
            <div class="ts-summary-placeholder-line shimmer short"></div>
            <div class="ts-summary-placeholder-line shimmer long"></div>
          </div>
          <div v-else v-html="eventList.meta.summary"></div>
        </v-card-text>

      </v-card>
    </div>


    <div v-if="eventList.objects.length || searchInProgress">
      <v-data-table
        id="tsEventTable"
        v-model="selectedEvents"
        :headers="headers"
        :items="eventList.objects"
        :footer-props="{ 'items-per-page-options': [10, 40, 80, 100, 200, 500], 'show-current-page': true }"
        :loading="searchInProgress"
        :options.sync="tableOptions"
        :server-items-length="totalHitsForPagination"
        item-key="_id"
        loading-text="Searching... Please wait"
        show-select
        disable-filtering
        must-sort
        :sort-desc.sync="sortOrderAsc"
        @update:sort-desc="sortEvents"
        @click:row="toggleDetailedEvent"
        sort-by="_source.timestamp"
        :hide-default-footer="totalHits < 11 || disablePagination"
        :expanded="expandedRows"
        :dense="displayOptions.isCompact"
        fixed-header
      >
          <template v-slot:top="{ pagination, options, updateOptions }">
            <v-toolbar dense flat color="transparent">
              <div v-if="!selectedEvents.length">
                <span style="display: inline-block; min-width: 200px">
                  <small>{{ fromEvent }}-{{ toEvent }} of {{ totalHits }} events ({{ totalTime }}s)</small>
                </span>

                <template>
                  <v-btn icon @click="saveSearchMenu = true" v-if="!disableSaveSearch">
                    <v-icon title="Save current search">mdi-content-save-outline</v-icon>
                  </v-btn>
                </template>

                <template>
                  <v-btn icon @click="showHistogram = !showHistogram" v-if="!disableHistogram">
                    <v-icon title="Toggle event histogram">mdi-chart-bar</v-icon>
                  </v-btn>
                </template>

                <v-dialog v-model="columnDialog" v-if="!disableColumns" max-width="500px" scrollable>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn id="tsModifyColumnsBtn" small plain rounded v-bind="attrs" v-on="on">
                      Edit Columns
                    </v-btn>
                  </template>

                  <v-card height="50vh">
                    <v-card-title>Select columns</v-card-title>

                    <v-card-text>
                      <v-text-field
                        v-model="searchColumns"
                        append-icon="mdi-magnify"
                        label="Search"
                        single-line
                        hide-details
                      ></v-text-field>
                      <br />
                      <v-data-table
                        v-model="selectedFields"
                        :headers="columnHeaders"
                        :items="availableColumns"
                        :search="searchColumns"
                        :hide-default-footer="true"
                        item-key="field"
                        disable-pagination
                        show-select
                        dense
                        @input="updateSelectedFields"
                      >
                        <template v-slot:item.field="{ item }">
                          {{ formatAttributeLabel(item.field) }}
                        </template>
                      </v-data-table>
                    </v-card-text>

                    <v-divider></v-divider>

                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn text @click="selectedFields = [{ field: 'event_type_msg', type: 'text' }, { field: 'norm__model_name', type: 'text' }]"> Reset </v-btn>
                      <v-btn text color="primary" @click="columnDialog = false"> Set columns </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
<!-- 
                <v-btn icon @click="exportSearchResult()">
                  <v-icon title="Download current view as CSV">mdi-download</v-icon>
                </v-btn> TODO --> 

                <v-menu v-if="!disableSettings" offset-y :close-on-content-click="false">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon title="View settings">mdi-dots-horizontal</v-icon>
                    </v-btn>
                  </template>

                  <v-card outlined max-width="475" class="mx-auto">
                    <v-list subheader two-line flat dense>
                      <v-subheader>Density</v-subheader>

                      <v-list-item-group>
                        <v-list-item :ripple="false">
                          <template>
                            <v-list-item-action>
                              <v-radio-group v-model="displayOptions.isCompact">
                                <v-radio :value="false"></v-radio>
                              </v-radio-group>
                            </v-list-item-action>

                            <v-list-item-content>
                              <v-list-item-title>Comfortable</v-list-item-title>
                              <v-list-item-subtitle>More space between rows</v-list-item-subtitle>
                            </v-list-item-content>
                          </template>
                        </v-list-item>

                        <v-list-item :ripple="false">
                          <template>
                            <v-list-item-action>
                              <v-radio-group v-model="displayOptions.isCompact">
                                <v-radio :value="true"></v-radio>
                              </v-radio-group>
                            </v-list-item-action>

                            <v-list-item-content>
                              <v-list-item-title>Compact</v-list-item-title>
                              <v-list-item-subtitle>Less space between rows</v-list-item-subtitle>
                            </v-list-item-content>
                          </template>
                        </v-list-item>
                      </v-list-item-group>
                      <v-divider></v-divider>

                      <v-list subheader two-line flat>
                        <v-subheader>Misc</v-subheader>
                        <v-list-item-group>
                          <v-list-item :ripple="false">
                            <v-list-item-action>
                              <v-switch dense color="" v-model="displayOptions.showTags"></v-switch>
                            </v-list-item-action>
                            <v-list-item-content>
                              <v-list-item-title>Tags</v-list-item-title>
                              <v-list-item-subtitle>Show tags</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list-item-group>
                        <v-list-item-group>
                          <v-list-item :ripple="false">
                            <v-list-item-action>
                              <v-switch dense v-model="displayOptions.showDataExportName"></v-switch>
                            </v-list-item-action>
                            <v-list-item-content>
                              <v-list-item-title>Data export name</v-list-item-title>
                              <v-list-item-subtitle>Show data export name</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list-item-group>
                        <v-list-item-group>
                          <v-list-item :ripple="false">
                            <v-list-item-action>
                              <v-switch dense v-model="displayOptions.showSourceFile"></v-switch>
                            </v-list-item-action>
                            <v-list-item-content>
                              <v-list-item-title>Source file</v-list-item-title>
                              <v-list-item-subtitle>Show originating file name</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list-item-group>
                      </v-list>
                    </v-list>
                  </v-card>
                </v-menu>
              </div>
              <div v-else class="actions">
                <small class="mr-2">Actions:</small>
                <v-btn x-small outlined @click="toggleMultipleStars()">
                  <v-icon left color="amber">mdi-star</v-icon>
                  Toggle star
                </v-btn>

                <v-menu v-model="showEventTagMenu" offset-x :close-on-content-click="false">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn x-small outlined v-bind="attrs" v-on="on">
                      <v-icon left>mdi-tag-plus-outline</v-icon>
                      Modify Tags
                    </v-btn>
                  </template>

                  <event-tag-dialog :events="selectedEvents" @close="showEventTagMenu = false"></event-tag-dialog>

                </v-menu>

              </div>

              <v-spacer></v-spacer>

              <v-data-footer
                v-if="totalHits > 11 && !disablePagination"
                :pagination="pagination"
                :options="options"
                @update:options="updateOptions"
                :show-current-page="true"
                :items-per-page-options="[10, 40, 80, 100, 200, 500]"
                items-per-page-text="Rows per page:"
                style="border: 0"
                class="mr-n3"
              ></v-data-footer>
            </v-toolbar>

            <v-card v-if="showHistogram" outlined class="my-3">
              <v-toolbar dense flat color="transparent">
                <v-spacer></v-spacer>
                <v-btn v-if="timeFilterChips.length" text color="primary" @click="removeChips(timeFilterChips)">
                  reset
                </v-btn>
                <v-btn icon @click="showHistogram = false">
                  <v-icon title="Close histogram">mdi-close</v-icon>
                </v-btn>
              </v-toolbar>
              <ts-bar-chart
                :chart-data="eventList.meta.count_over_time"
                @addChip="addChipFromHistogram($event)"
              ></ts-bar-chart>
            </v-card>
          </template>

          <!-- Event details -->
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length">
              <!-- Details -->
              <v-container v-if="item.showDetails" fluid class="mt-4">
                <ts-event-detail :event="item" :availableColumns="availableColumns"></ts-event-detail>
              </v-container>

              <!-- Time bubble -->
              <v-divider v-if="item.showDetails && item.deltaDays"></v-divider>
              <div v-if="item.deltaDays > 0" class="ml-7">
                <div
                  class="ts-time-bubble-vertical-line ts-time-bubble-vertical-line-color"
                  v-bind:style="getTimeBubbleColor(item)"
                ></div>
                <div class="ts-time-bubble ts-time-bubble-color" v-bind:style="getTimeBubbleColor(item)">
                  <div class="ts-time-bubble-text">
                    <b>{{ item.deltaDays | compactNumber }}</b> days
                  </div>
                </div>
                <div
                  class="ts-time-bubble-vertical-line ts-time-bubble-vertical-line-color"
                  v-bind:style="getTimeBubbleColor(item)"
                ></div>
              </div>
            </td>
          </template>

          <!-- Actions field -->
          <template v-slot:item.actions="{ item }">
            <div id="tsAnnotateActions" style="display: inline-flex; gap: 4px; align-items: center;">
              <v-btn id="tsEventActions" small icon @click="toggleStar(item)">
                <v-icon title="Toggle star status" v-if="item._source.starred === 1" color="amber"
                  >mdi-star</v-icon
                >
                <v-icon title="Toggle star status" v-else>mdi-star-outline</v-icon>
              </v-btn>

              <!-- Tag menu -->
              <ts-event-tag-menu :event="item"></ts-event-tag-menu>
            </div>

            <!-- Action sub-menu -->
            <ts-event-action-menu :event="item" @showContextWindow="showContextWindow($event)"></ts-event-action-menu>
          </template>

          <!-- Datetime field with action buttons -->
          <template v-slot:item._source.primary_timestamp="{ item }">
            <!-- <div v-bind:style="getDataExportColor(item)" class="datetime-table-cell"> -->
            <div class="datetime-table-cell">
              <span v-if="isDisplayableTimestamp(item._source.primary_timestamp)">
                <!-- {{ item._source.primary_timestamp }} -->
                <!-- {{ (item._source.primary_timestamp | formatTimestamp | shortDateTime ) }} -->
                  {{ $options.filters.shortDateTimeLocal(item._source.primary_timestamp) }}
              </span>
              <span v-else class="text--secondary font-italic">N/A</span>
            </div>
          </template>

          <!-- Generic slot for any field type. Adds tags and emojis to the first column. -->
          <template v-for="(field, index) in headers" v-slot:[getFieldName(field.field)]="{ item }">
            <div
              :key="field.text"
              class="ts-event-field-container"
            >
              <span
                :class="{
                  // 'ts-event-field-ellipsis': field.text === 'category',
                  'ts-event-field-highlight': item._id === highlightEventId, 
                }"
              >
                <!-- Tags -->
                <span
                  v-if="
                    displayOptions.showTags &&
                    // index === 3 &&
                    (field.text === 'event_type_msg' || field.text === 'Event Type' || (index === 4 && headers[3].value === '_source.comment')) &&
                    (item._source.tags && item._source.tags.length > 0)
                  "
                >
                  <ts-event-tags :item="item" :tagConfig="tagConfig" :showDetails="item.showDetails"></ts-event-tags>
                </span>
                
                
                <span>{{ field.text === 'Event Type' ? item._source.event_type_msg : capitalize(item._source[field.field]) }}</span>
              </span>
            </div>
          </template>

          <!-- export name field -->
          <template v-slot:item.data_export_name="{ item }">
            <!-- <v-chip label style="margin-top: 1px; margin-bottom: 1px; font-size: 0.8em"> -->
            <v-chip label style="margin-top: 1px; margin-bottom: 1px; font-size: 0.8em" v-bind:style="getDataExportColor(item)">
              <span class="export-name-ellipsis" style="width: 100px; text-align: center">{{
                getDataExport(item).name
              }}</span></v-chip>
          </template>

          <template v-slot:item.source_file="{ item }">
            <v-chip label style="margin-top: 1px; margin-bottom: 1px; font-size: 0.8em" v-if="item._source.filename">
              <span class="export-name-ellipsis" style="max-width: 180px; text-align: center" :title="item._source.filename">
                {{ item._source.filename }}
              </span>
            </v-chip>
          </template>



          <!-- Comment field -->
          <!-- <template v-slot:item._source.comment="{ item }">
            <div class="d-inline-block">
              <v-btn icon small @click="toggleDetailedEvent(item)" v-if="item._source.comment && item._source.comment.length">
                <v-badge :offset-y="10" :offset-x="10" bordered :content="item._source.comment.length">
                  <v-icon :title="item['showDetails'] ? 'Close event &amp; comments' : 'Open event &amp; comments'" small>
                    mdi-comment-text-multiple-outline
                  </v-icon>
                </v-badge>
              </v-btn>
            </div>

            <div v-if="item['showDetails'] && (!item._source.comment || !item._source.comment.length) && !item.showComments" class="d-inline-block">
              <v-btn icon small @click="newComment(item)">
                <v-icon title="Add a comment"> mdi-comment-plus-outline </v-icon>
              </v-btn>
            </div>

            <div v-if="item['showDetails'] && (!item._source.comment || !item._source.comment.length) && item.showComments" class="d-inline-block">
              <v-btn icon small @click="item.showComments = false">
                <v-icon title="Close comments"> mdi-comment-remove-outline </v-icon>
              </v-btn>
            </div>
          </template> -->
        </v-data-table>
      </div>
    </div>
</template>

<script>
import DB from '@/database/index.js'
import EventBus from '@/event-bus.js'
import { formatAttributeLabel } from '@/filters/FormatAttributeLabel.js'
import { capitalize } from '@/filters/Capitalize.js'

import TsBarChart from './BarChart.vue'
import TsEventDetail from './EventDetail.vue'
import TsEventTagMenu from './EventTagMenu.vue'
import EventTagDialog from './EventTagDialog.vue'
import TsEventActionMenu from './EventActionMenu.vue'
import TsEventTags, { QUICK_TAG_CONFIG } from './EventTags.vue'
import WelcomeCard from './WelcomeCard.vue'
import SearchNotFoundCard from './SearchNotFoundCard.vue'

const defaultQueryFilter = () => {
  return {
    from: 0,
    terminate_after: 40,
    size: 40,
    uploadIds: ['_all'],
    order: 'desc',
    chips: [],
  }
}

const emptyEventList = () => {
  return {
    meta: {
      count_per_data_export: {},
      num_events: 0,
      num_states: 0,
      has_next_page: false,
    },
    objects: [],
  }
}

export default {
  components: {
    TsBarChart,
    TsEventDetail,
    TsEventTagMenu,
    EventTagDialog,
    TsEventActionMenu,
    TsEventTags,
    WelcomeCard,
    SearchNotFoundCard,
  },
  props: {
    queryRequest: {
      type: Object,
      default: () => {},
    },
    itemsPerPage: {
      type: Number,
      default: 40,
    },
    disableSaveSearch: {
      type: Boolean,
      default: true,
    },
    disableHistogram: {
      type: Boolean,
      default: true,
    },
    disableColumns: {
      type: Boolean,
      default: false,
    },
    disableSettings: {
      type: Boolean,
      default: false,
    },
    disablePagination: {
      type: Boolean,
      default: false,
    },
    highlightEvent: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      showEventTagMenu: false,
      columnHeaders: [
        {
          text: '',
          value: 'field',
        },
      ],
      tableOptions: {
        itemsPerPage: this.itemsPerPage,
      },
      isSummaryLoading: false,
      currentItemsPerPage: this.itemsPerPage,
      expandedRows: [],
      selectedFields: [
        { field: 'event_type_msg', type: 'text' },
        { field: 'norm__model_name', type: 'text' },
      ],
      searchColumns: '',
      columnDialog: false,
      saveSearchMenu: false,
      saveSearchFormName: '',
      saveSearchNameRules: [(v) => !!v || 'Name is required.', (v) => (v && v.length <= 255) || 'Name is too long.'],
      selectedEventTags: [],
      tagConfig: QUICK_TAG_CONFIG,
      searchInProgress: false,
      exportDialog: false,
      currentPage: 1,
      eventList: {
        meta: {},
        objects: [],
      },
      currentQueryString: '',
      currentQueryFilter: defaultQueryFilter(),
      selectedEvents: [],
      displayOptions: {
        isCompact: false,
        showTags: true,
        showMillis: false,
        showDataExportName: true,
        showSourceFile: false,
      },
      showHistogram: false,
      branchParent: null,
      sortOrderAsc: true,
      summaryCollapsed: false,
      showBanner: false,
      isFirstSearch: true,
    }
  },
  computed: {
    summaryInfoMessage() {
      const totalEvents = this.eventList.meta.summary_event_count
      const uniqueEvents = this.eventList.meta.summary_unique_event_count
      return `[experimental] This summary is based on the message field on your current page (${totalEvents} rows, ${uniqueEvents} unique message fields).`
    },
    project() {
      return this.$store.state.project
    },
    meta() {
      return this.$store.state.meta
    },
    highlightEventId() {
      if (this.highlightEvent) {
        return this.highlightEvent._id
      }
      return null
    },
    totalHits() {
      // Use total_count from metadata (represents ALL matching events, not just current page)
      return this.eventList.meta.total_count || 0;
    },
    totalHitsForPagination() {
      // Return actual total hits from the database
      // Vuetify uses this for the "1-100 of X" display
      return this.totalHits;
    },
    totalTime() {
      return this.eventList.meta.query_time_ms / 1000 || 0
    },
    fromEvent() {
      return this.currentQueryFilter.from || 1
    },
    toEvent() {
      if (this.totalHits < this.currentQueryFilter.size) {
        return
      }
      return parseInt(this.currentQueryFilter.from) + parseInt(this.currentQueryFilter.size)
    },
    timeFilterChips: function () {
      return this.currentQueryFilter.chips.filter((chip) => chip && chip.type && chip.type.startsWith('datetime'))
    },
    currentSearchNode() {
      return this.$store.state.currentSearchNode
    },
    userSettings() {
      return this.$store.state.settings
    },
    headers() {
      let baseHeaders = [
        {
          text: '',
          value: 'data-table-select',
          sortable: false,
        },
        {
          value: 'actions',
          width: '105',
          sortable: false,
        },
        {
          text: `Datetime (${this.$store.state.localTimezoneAbbr}) `,
          align: 'start',
          value: '_source.primary_timestamp',
          width: '200',
          sortable: true,
        },
        // {
        //   value: '_source.comment',
        //   width: '40',
        //   sortable: false,
        // },
      ]
      let extraHeaders = []
      this.selectedFields.forEach((field) => {
        let header = {
          text: formatAttributeLabel(field.field),
          align: 'start',
          value: '_source.' + field.field,
          field: field.field,
          sortable: false,
        }
        if (field.field === 'event_type_msg') {
          header.width = '100%'
          extraHeaders.unshift(header)
        } else if (field.field === 'norm__model_name') {
          header.width = '120'
          extraHeaders.push(header)
        } else {
          header.width = '130'
          extraHeaders.push(header) // Positions next to platform chip
        }
      })

      // Extend the column headers from position 3 (after the actions column)
      baseHeaders.splice(3, 0, ...extraHeaders)

      // Add export name based on configuration
      if (this.displayOptions.showDataExportName) {
        baseHeaders.push({
          value: 'data_export_name',
          align: 'end',
          sortable: false,
        })
      }
      if (this.displayOptions.showSourceFile) {
        baseHeaders.push({
          value: 'source_file',
          text: 'Source file',
          align: 'end',
          sortable: false,
        })
      }
      return baseHeaders
    },

    activeContext() {
      return this.$store.state.activeContext
    },
    settings() {
      return this.$store.state.settings
    },
    filterChips: function () {
      return this.currentQueryFilter.chips.filter((chip) => chip && chip.type && (chip.type === 'label' || chip.type === 'attribute'))
    },
    availableColumns() {
      if (!this.meta || !this.meta.mappings) return [];
      const excluded = ['id', 'event_category', 'tags', 'labels', 'event_kind', 'data_export_id', 'event_outcome'];
      return this.meta.mappings.filter(m => !excluded.includes(m.field));
    },
  },
  methods: {
    capitalize,
    formatAttributeLabel,
    isDisplayableTimestamp(timestamp) {
      const numericTimestamp = Number(timestamp)
      return Number.isFinite(numericTimestamp) && numericTimestamp > 0
    },
    toggleCompareEvent(item) {
      this.$store.commit('TOGGLE_COMPARE_EVENT', item)
    },
    isEventSelectedForCompare(item) {
      const compareEvents = this.$store.state.compareEvents || []
      return compareEvents.some((e) => e._id === item._id)
    },
    toggleSummary() {
          this.summaryCollapsed = !this.summaryCollapsed;
          localStorage.setItem('aiSummaryCollapsed', String(this.summaryCollapsed));
    },

    sortEvents(sortDesc) {
      // sortDesc is the value of Vuetify's sort-desc (true = descending, false = ascending)
      if (sortDesc) {
        this.currentQueryFilter.order = 'desc'
      } else {
        this.currentQueryFilter.order = 'asc'
      }
      this.search(true, true, false)
    },
    getFieldName: function (field) {
      if (field === 'Event Type' || field === 'event_type_msg') {
        return 'item._source.event_type_msg'
      }
      return 'item._source.' + field
    },
    toggleDetailedEvent: function (row) {
      let index = this.expandedRows.findIndex((x) => x._id === row._id)
      if (this.expandedRows.some((event) => event._id === row._id)) {
        if (row.showDetails) {
          row['showDetails'] = false
          this.expandedRows.splice(index, 1)
          this.$set(row, 'showComments', false)
        } else {
          row['showDetails'] = true
          this.expandedRows.splice(index, 1)
          this.expandedRows.push(row)
          if (this.$store.state.demoMode) {
            EventBus.$emit('demo:action', 'event-expanded')
          }
          return
        }

        if (row.deltaDays) {
          this.expandedRows.splice(index, 1)
          this.expandedRows.push(row)
        }
      } else {
        row['showDetails'] = true
        this.expandedRows.push(row)
        if (this.$store.state.demoMode) {
          EventBus.$emit('demo:action', 'event-expanded')
        }
      }
    },
    newComment: function (row) {
      if (row.showDetails) {
        this.$set(row, 'showComments', true)
      } else {
        this.$set(row, 'showComments', true)
        this.toggleDetailedEvent(row)
      }
    },
    addTimeBubbles: function () {
      this.expandedRows = []
      this.eventList.objects.forEach((event, index) => {
        if (index < 1) {
          return
        }
        let prevEvent = this.eventList.objects[index - 1]
        let timestampMillis = this.$options.filters.formatTimestamp(event._source.primary_timestamp)
        let prevTimestampMillis = this.$options.filters.formatTimestamp(prevEvent._source.primary_timestamp)
        let timestamp = Math.floor(timestampMillis / 1000)
        let prevTimestamp = Math.floor(prevTimestampMillis / 1000)
        let delta = Math.floor(timestamp - prevTimestamp)
        // In descending order (most recent first), prevEvent has later timestamp, so flip the delta
        if (this.currentQueryFilter.order === 'desc') {
          delta = Math.floor(prevTimestamp - timestamp)
        }
        let deltaDays = Math.floor(delta / 60 / 60 / 24)
        if (deltaDays > 0) {
          prevEvent['deltaDays'] = deltaDays
          this.expandedRows.push(prevEvent)
        }
      })
    },
    getDataExport: function (event) {
      // Browser model: events have data_export_id directly
      const deID = event._source.data_export_id
      const dataExport = this.project.dataExports.find((de) => de.id === deID)
      if (!dataExport) {
        console.warn('[EventList.getDataExport] Data export not found for id:', deID)
        return { name: 'Unknown Data Export', id: deID, color: '#999' }
      }
      return dataExport
    },
    getDataExportColor(event) {
      let dataExport = this.getDataExport(event)
      let backgroundColor = dataExport.color || '#999'
      if (!backgroundColor.startsWith('#')) {
        backgroundColor = '#' + backgroundColor
      }
      if (this.$vuetify.theme.dark) {
        return {
          'background-color': backgroundColor,
          filter: 'grayscale(25%)',
          color: '#333',
        }
      }
      return {
        'background-color': backgroundColor,
      }
    },
    getTimeBubbleColor() {
      let backgroundColor = '#f5f5f5'
      if (this.$vuetify.theme.dark) {
        backgroundColor = '#333'
      }
      return {
        'background-color': backgroundColor,
      }
    },
    getAllUploadIds: function () {
      // Browser model: return IDs directly
      return this.project.dataExports.map((de) => de.id)
    },
    search: async function (resetPagination = true, incognito = false, parent = false) {
      // Exit early if there are no uploadIds selected
      if (this.currentQueryFilter.uploadIds && !this.currentQueryFilter.uploadIds.length) {
        this.eventList = emptyEventList()
        EventBus.$emit('updateCountPerExport', {})
        this.$emit('countPerDataExport', {})
        EventBus.$emit('searchResultsCounts', {
          countPerEventType: {},
          countPerIPAddress: {},
          countPerTagOrLabel: {}
        })
        return
      }

      // Expand '_all' to all data export IDs (handle both array ['_all'] and string '_all')
      const uploadIds = this.currentQueryFilter.uploadIds;
      if (
        uploadIds === '_all' || 
        (Array.isArray(uploadIds) && (uploadIds.length === 0 || uploadIds[0] === '_all'))
      ) {
        const allUploadIds = this.getAllUploadIds();
        this.currentQueryFilter.uploadIds = allUploadIds;
      }

      // Allow searches with empty query string (shows all events from selected data export)
      this.searchInProgress = true
      this.selectedEvents = []
      this.eventList = emptyEventList()

      if (resetPagination) {
        this.tableOptions.page = 1
        this.currentPage = 1
        this.currentItemsPerPage = this.tableOptions.itemsPerPage
        this.currentQueryFilter.size = this.tableOptions.itemsPerPage
        this.currentQueryFilter.from = 0
      }

      const startTime = Date.now()
      
      try {
        const response = await DB.searchEvents(this.$route.meta.dbName || 'userdata', this.currentQueryString, this.currentQueryFilter)

        // Response has unwrapped format:
        // - objects: array of {_id, _source} wrapped events
        // - meta: metadata including count_per_data_export, total_count, etc.
        this.eventList.objects = response.objects || []
        this.eventList.meta = response.meta || {
          count_per_data_export: {},
          total_count: 0,
        }
        
        // Calculate has_next_page based on pagination
        const currentFrom = this.currentQueryFilter.from || 0
        this.eventList.meta.has_next_page = (currentFrom + this.eventList.objects.length) < this.eventList.meta.total_count
        this.eventList.meta.query_time_ms = Date.now() - startTime

        this.updateShowBanner()
        this.$emit('countPerDataExport', this.eventList.meta.count_per_data_export)
        EventBus.$emit('updateCountPerExport', this.eventList.meta.count_per_data_export)
        EventBus.$emit('searchResultsCounts', {
          countPerEventType: this.eventList.meta.count_per_event_type || {},
          countPerIPAddress: this.eventList.meta.count_per_ip_address || {},
          countPerTagOrLabel: this.eventList.meta.count_per_tag_or_label || {}
        })
        
        this.addTimeBubbles()
        
        if (this.isFirstSearch) {
          this.isFirstSearch = false
          this.$emit('initialSearchComplete')
        }
      } catch (error) {
        console.error('[EventList] Error searching:', error)
        this.errorSnackBar('Error fetching search results: ' + error.message)
      } finally {
        this.searchInProgress = false
      }
    },
    exportSearchResult: function () {
      // TODO: Implement CSV export for browser model
      this.exportDialog = false
    },
    addChip: function (chip) {
      if (!this.currentQueryFilter.chips) {
        this.currentQueryFilter.chips = []
      }
      this.currentQueryFilter.chips.push(chip)
      this.search()
    },
    removeChip: function (chip, search = true) {
      let chipIndex = this.currentQueryFilter.chips.findIndex((c) => c.value === chip.value)
      this.currentQueryFilter.chips.splice(chipIndex, 1)
      if (search) {
        this.search()
      }
    },
    removeChips: function (chips, search = true) {
      if (!Array.isArray(chips)) {
        this.errorSnackBar('Not an array of chips')
        return
      }
      chips.forEach((chip) => {
        this.removeChip(chip, false)
      })
      if (search) {
        this.search()
      }
    },
    addChipFromHistogram: function (chip) {
      if (!this.currentQueryFilter.chips) {
        this.currentQueryFilter.chips = []
      }
      this.currentQueryFilter.chips.forEach((chip) => {
        if (chip.type === 'datetime_range') {
          this.removeChip(chip, false)
        }
      })
      this.addChip(chip)
    },
    paginate: function () {
      // Reset pagination if number of pages per page changes.
      if (this.tableOptions.itemsPerPage !== this.currentItemsPerPage) {
        this.tableOptions.page = 1
        this.currentPage = 1
        this.currentItemsPerPage = this.tableOptions.itemsPerPage
        this.currentQueryFilter.size = this.tableOptions.itemsPerPage
        this.search(true, true)
        return
      }
      // To avoid double search request exit early if this is the first search for this
      // search session.
      if (this.currentPage === this.tableOptions.page) {
        return
      }
      this.currentQueryFilter.from =
        this.tableOptions.page * this.currentQueryFilter.size - this.currentQueryFilter.size
      this.currentPage = this.tableOptions.page
      this.search(false, true)
    },
    updateSelectedFields: function (value) {
      // If we haven't fetched the field before, do an new search.
      value.forEach((field) => {
        if (!this.headers.filter((e) => e.field === field.field).length > 0) {
          this.search(true, true)
        }
      })
    },
    removeField: function (index) {
      this.selectedFields.splice(index, 1)
    },
    toggleStar(event) {
      // Ensure labels array exists
      if (!event._source.labels) {
        event._source.labels = []
      }
      
      // Determine if we're adding or removing the star
      // Determine if we're adding or removing the star
      const isStarred = event._source.starred === 1;
      
      if (isStarred) {
        event._source.starred = 0
        if (event._source.labels) {
          const idx = event._source.labels.indexOf('starred')
          if (idx > -1) event._source.labels.splice(idx, 1)
        }
        this.$store.dispatch('updateEventLabels', { label: 'starred', num: -1 })
        DB.removeLabelEvent(this.$route.meta.dbName || 'userdata', [event._id], ['starred']).catch(e => {
          console.error('Error updating star in database:', e)
        })
      } else {
        event._source.starred = 1
        if (!event._source.labels) {
          event._source.labels = []
        }
        event._source.labels.push('starred')
        this.$store.dispatch('updateEventLabels', { label: 'starred', num: 1 })
        DB.addLabelEvent(this.$route.meta.dbName || 'userdata', [event._id], ['starred']).catch(e => {
          console.error('Error updating star in database:', e)
        })
      }
    },
    toggleMultipleStars: function () {
      let netStarCountChange = 0
      const eventsToStar = []
      const eventsToUnstar = []
      
      this.selectedEvents.forEach((event) => {
        if (event._source.starred === 1) {
          event._source.starred = 0
          if (event._source.labels) {
            const idx = event._source.labels.indexOf('starred')
            if (idx > -1) event._source.labels.splice(idx, 1)
          }
          eventsToUnstar.push(event._id)
          netStarCountChange--
        } else {
          event._source.starred = 1
          if (!event._source.labels) {
            event._source.labels = []
          }
          event._source.labels.push('starred')
          eventsToStar.push(event._id)
          netStarCountChange++
        }
      })
      
      // Update global store reactive count
      if (netStarCountChange !== 0) {
        this.$store.dispatch('updateEventLabels', { label: 'starred', num: netStarCountChange })
      }
      
      // Persist changes to database
      if (eventsToStar.length > 0) {
        DB.addLabelEvent(this.$route.meta.dbName || 'userdata', eventsToStar, ['starred']).catch(e => {
          console.error('Error starring events:', e)
        })
      }
      if (eventsToUnstar.length > 0) {
        DB.removeLabelEvent(this.$route.meta.dbName || 'userdata', eventsToUnstar, ['starred']).catch(e => {
          console.error('Error unstarring events:', e)
        })
      }
      this.selectedEvents = []
    },
    saveSearch: function () {
      // Saved searches not implemented in browser-only version
      this.$store.dispatch('setSnackBar', {
        message: 'Saved searches are not available in browser-only mode',
        color: 'info',
        timeout: 3000,
      })
      this.saveSearchFormName = ''
      this.saveSearchMenu = false
    },
    updateShowBanner: function() {
      // Show banner only when processing a are enabled and at
      // least one enabled export is in "processing" state.
      // Browser model: status is a string, not an array
      this.showBanner =
        !!this.settings.showProcessingData &&
        this.project.dataExports
          .filter(tl => this.$store.state.enabledDataExports.includes(tl.id))
          .some(tl => tl.status === 'processing')
    },

  },
  watch: {
    tableOptions: {
      handler(newVal, oldVal) {
        // Return early if the sort order changed.
        // The search is done in the sortEvents method.
        if (oldVal.sortDesc === undefined) return
        if (newVal.sortDesc[0] !== oldVal.sortDesc[0]) return

        this.paginate()
      },
      deep: true,
    },
    queryRequest: {
      handler(newQueryRequest, oldqueryRequest) {
        // Return early if this isn't a new request.
        if (newQueryRequest === oldqueryRequest || !newQueryRequest) {
          return
        }
        this.currentQueryString = newQueryRequest.queryString || ''
        this.currentQueryFilter = newQueryRequest.queryFilter || defaultQueryFilter()
        this.currentQueryDsl = newQueryRequest.queryDsl || null
        let resetPagination = newQueryRequest['resetPagination'] || false
        let incognito = newQueryRequest['incognito'] || false
        let parent = newQueryRequest['parent'] || false
        // Set additional fields. This is used when loading filter from a saved search.
        if (this.currentQueryFilter.fields) {
          this.selectedFields = this.currentQueryFilter.fields
        }
        // Preserve user defined sort order.
        if (this.sortOrderAsc) {
          this.currentQueryFilter.order = 'asc'
        } else {
          this.currentQueryFilter.order = 'desc'
        }
        this.search(resetPagination, incognito, parent)
      },
      deep: true,
    },
    'settings.showProcessingData': {
      handler() {
        this.updateShowBanner()
      },
    },
  },
  created() {
    const storedState = localStorage.getItem('aiSummaryCollapsed');
    if (storedState === 'true') {
      this.summaryCollapsed = true;
    }
    
    if (this.queryRequest && this.queryRequest.queryString) {
      this.currentQueryString = this.queryRequest.queryString
      this.currentQueryFilter = { ...this.queryRequest.queryFilter } || defaultQueryFilter()
      this.currentQueryDsl = { ...this.queryRequest.queryDsl }
      if (this.currentQueryFilter.fields) {
        this.selectedFields = this.currentQueryFilter.fields
      }
    }
    
    this.search()
  },
  mounted() {
    // Demo mode: allow programmatic row expansion/collapse
    EventBus.$on('demo:force-expand-first-row', () => {
        const attemptExpand = () => {
            if (this.eventList.objects.length > 0 && !this.searchInProgress) {
                const first = this.eventList.objects[0]
                if (!this.expandedRows.some(r => r._id === first._id)) {
                    this.toggleDetailedEvent(first)
                }
                return true
            }
            return false
        }

        if (!attemptExpand()) {
            // If data is not ready, retry for up to 2 seconds
            let retries = 0
            const interval = setInterval(() => {
                retries++
                if (attemptExpand() || retries > 20) {
                    clearInterval(interval)
                }
            }, 100)
        }
    })
    EventBus.$on('demo:force-collapse-all', () => {
        this.expandedRows = []
    })
  },
  beforeDestroy() {
    EventBus.$off('demo:force-expand-first-row')
    EventBus.$off('demo:force-collapse-all')
  }
}
</script>


<style lang="scss">

.ts-event-field-container {
  position: relative;
  max-width: 100%;
  padding: 0 !important;
  display: -webkit-flex;
  display: -moz-flex;
  display: flex;
  vertical-align: text-bottom !important;
}

.ts-event-field-ellipsis {
  position: absolute;
  white-space: nowrap;
  overflow-y: visible;
  overflow-x: hidden;
  text-overflow: ellipsis;
  -ms-text-overflow: ellipsis;
  -o-text-overflow: ellipsis;
  max-width: 100%;
  min-width: 0;
  width: 100%;
  top: 50%;
  transform: translateY(-50%);
  left: 0;
}

.ts-event-field-highlight {
  font-weight: bold;
  color: red;
}

.v-data-table__expanded.v-data-table__expanded__content {
  box-shadow: none !important;
}

.ts-time-bubble {
  width: 120px;
  height: 25px;
  border-radius: 20px;
  position: relative;
  margin: 0 0 0 136px;
  text-align: center;
  font-size: var(--font-size-small);
}

.ts-time-bubble-text {
  font-size: 0.8em;
  padding-top: 4px;
}

.ts-time-bubble-vertical-line {
  width: 2px;
  height: 15px;
  margin: 0 0 0 194px;
  background-color: #f5f5f5;
}

.datetime-table-cell {
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

// Adjust padding for event data table
.v-data-table td,
th {
  padding: 0 10px 0 0 !important;
}

.v-data-table td:last-child,
th:last-child {
  padding: 0 !important;
}

.v-data-table td:first-child,
th:first-child {
  padding: 0 0 0 10px !important;
}

.ts-ai-summary-card {
  border: 1px solid transparent !important;
  border-radius: 8px;
  background-color: #fafafa;
  background-image:
      linear-gradient(white, white),
      linear-gradient(90deg,
          #8ab4f8 0%,
          #81c995 20%,
          #f8c665 40%,
          #ec7764 60%,
          #b39ddb 80%,
          #8ab4f8 100%
      );
  background-origin: border-box;
  background-clip: content-box, border-box;
  background-size: 300% 100%;
  animation: borderBeamIridescent-subtle 6s linear infinite;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08);
  display: block;
  margin-bottom: 20px;
}

.v-data-table {
  display: block; /* Ensure block display for data table */
}

@keyframes borderBeamIridescent-subtle {
    0% {
        background-position: 0% 50%;
    }
    100% {
        background-position: 100% 50%;
    }
}

.theme--dark.ts-ai-summary-card {
  background-color: #1e1e1e;
  border-color: hsla(0,0%,100%,.12) !important;
  background-image:
      linear-gradient(#1e1e1e, #1e1e1e),
      linear-gradient(90deg,
          #8ab4f8 0%,
          #81c995 20%,
          #f8c665 40%,
          #ec7764 60%,
          #b39ddb 80%,
          #8ab4f8 100%
      );
      box-shadow: 0 2px 5px rgba(255, 255, 255, 0.08);
  display: block;
  margin-bottom: 20px;
}

.ts-ai-summary-text {
  white-space: pre-line;
  word-wrap: break-word;
  overflow-wrap: anywhere;
  margin-top: -10px;
  padding-left: 10px;
  padding-right: 10px;
}

.ts-ai-summary-card .v-btn--icon {
  cursor: pointer;
}

.ts-ai-summary-card .v-btn--icon:hover {
  opacity: 0.8;
}

.ts-summary-placeholder-line {
  height: 1em;
  background-color: #e0e0e0;
  margin-bottom: 0.5em;
  border-radius: 4px;
  width: 100%;
}

.ts-summary-placeholder-line.short {
  width: 60%;
}

.ts-summary-placeholder-line.long {
  width: 80%;
}

.shimmer {
  background: linear-gradient(to right, #e0e0e0 8%, #f0f0f0 18%, #e0e0e0 33%);
  background-size: 800px 100%;
  animation: shimmer-animation 1.5s infinite linear forwards;
}

@keyframes shimmer-animation {
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
}

.event-list-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 20px;
}

::v-deep .no-transition {
  transition: none !important;
}

.ts-ai-summary-card-title {
  display: flex;
  align-items: baseline;
}

.ts-ai-summary-title {
  margin-right: 8px;
  font-weight: normal;
}

.ts-ai-summary-subtitle {
  font-size: 0.7em;
  color: grey;
  vertical-align: middle;
  display: inline-block;
}

.actions button {
  margin-right: 10px;
}
</style>
